import React from 'react'
import { PurchaseAssistOffer } from '@prometheus/types'
import { truncateCash } from '@prometheus/utils'
import { Button, Card, CardBody, CardFooter, CardHeader, Modal, Stack, Typography } from '@prometheus/components'
import { acceptLabel, offerTitle, withdrawalPlan } from './purchaseAssist'
import './PurchaseAssistModal.scss'

interface PurchaseAssistModalProps {
  offer: PurchaseAssistOffer
  /** What the player was trying to buy, e.g. "Buy Planet". */
  actionLabel?: string
  submitting?: boolean
  onAccept: () => void
  onCancel: () => void
}

export const PurchaseAssistModal = ({ offer, actionLabel, submitting, onAccept, onCancel }: PurchaseAssistModalProps) => {
  const plan = withdrawalPlan(offer)

  return (
    <Modal onClose={onCancel}>
      <Card padding="sm" className="pg-purchase-assist" style={{ width: 'min(420px, calc(100vw - 20px))' }}>
        <CardHeader>
          <Typography variant="h4" retro color="warning">{offerTitle(offer)}</Typography>
        </CardHeader>
        <CardBody>
          <Stack space="sm">
            {offer.cash_shortfall > 0 && (
              <Typography>
                {actionLabel ? `${actionLabel} is ` : 'This is '}
                <strong>{truncateCash(offer.cash_shortfall)}</strong> more than you have on hand.
              </Typography>
            )}

            {offer.population_shortfall > 0 && (
              <Typography>
                You are <strong>{truncateCash(offer.population_shortfall, { raw: true })}</strong> crew short
                {offer.population_hire_cost > 0 && (
                  <> — hiring them costs <strong>{truncateCash(offer.population_hire_cost)}</strong></>
                )}
                .
              </Typography>
            )}

            {!!plan.length && (
              <Card padding="sm" borderColor="secondary">
                <Stack space="xs">
                  <Typography variant="h5" retro color="primary">
                    Withdraw {truncateCash(offer.cash_to_raise)}
                  </Typography>
                  {plan.map(entry => (
                    <div key={entry.source} className="pg-purchase-assist__row">
                      <Typography component="span">{entry.label}</Typography>
                      <Typography component="span" bold>
                        {entry.approximate ? 'up to ' : ''}{truncateCash(entry.amount)}
                      </Typography>
                    </div>
                  ))}
                </Stack>
              </Card>
            )}

            <Typography color="text-muted">
              {offer.population_shortfall > 0 && offer.cash_to_raise > 0
                ? 'Accept and the money comes out of your accounts, the crew is hired, and the purchase goes through.'
                : offer.population_shortfall > 0
                  ? 'Accept and the crew is hired, then the purchase goes through.'
                  : 'Accept and the money comes out of your accounts, then the purchase goes through.'}
            </Typography>
          </Stack>
        </CardBody>
        <CardFooter>
          <Button variant="danger" outline onClick={onCancel} disabled={submitting}>Cancel</Button>
          <Button variant="primary" onClick={onAccept} disabled={submitting}>
            {acceptLabel(offer, actionLabel)}
          </Button>
        </CardFooter>
      </Card>
    </Modal>
  )
}

export default PurchaseAssistModal
